import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useStore } from '../../store/useStore'
import MediaDisplay from '../ui/MediaDisplay'
import GlassCard from '../ui/GlassCard'

const BannerCarousel = () => {
  const { banners } = useStore()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedBanner, setSelectedBanner] = useState(null)

  // Debug: Afficher les bannières dans la console
  useEffect(() => {
    console.log('BannerCarousel - Bannières chargées:', banners)
  }, [banners])

  // Filtrer seulement les bannières actives
  const activeBanners = banners.filter(banner => 
    banner.isActive && 
    new Date(banner.startDate) <= new Date() && 
    new Date(banner.endDate) >= new Date()
  )

  console.log('BannerCarousel - Bannières actives:', activeBanners)

  // Auto-play des bannières
  useEffect(() => {
    if (activeBanners.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === activeBanners.length - 1 ? 0 : prevIndex + 1
      )
    }, 5000) // Change toutes les 5 secondes

    return () => clearInterval(interval)
  }, [activeBanners.length])

  const nextBanner = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === activeBanners.length - 1 ? 0 : prevIndex + 1
    )
  }

  const prevBanner = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? activeBanners.length - 1 : prevIndex - 1
    )
  }

  const openModal = (banner) => {
    setSelectedBanner(banner)
    setIsModalOpen(true)
  }

  if (activeBanners.length === 0) {
    return null
  }

  return (
    <div className="relative">
      {/* Carousel Principal */}
      <div className="relative h-96 md:h-[500px] overflow-hidden rounded-2xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <div 
              className="w-full h-full cursor-pointer relative"
              onClick={() => openModal(activeBanners[currentIndex])}
            >
              <MediaDisplay
                src={activeBanners[currentIndex].imageUrl}
                alt={activeBanners[currentIndex].title}
                className="w-full h-full"
                showControls={false}
              />
              
              {/* Overlay avec informations */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  {activeBanners[currentIndex].title}
                </h2>
                <p className="text-white/90 text-sm md:text-lg max-w-2xl">
                  {activeBanners[currentIndex].description}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        {activeBanners.length > 1 && (
          <>
            <button
              onClick={prevBanner}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-2 rounded-full text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextBanner}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-2 rounded-full text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Indicateurs */}
        {activeBanners.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {activeBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de détail */}
      <AnimatePresence>
        {isModalOpen && selectedBanner && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 cursor-pointer"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <GlassCard className="p-0 overflow-hidden">
                  <div className="relative">
                    <MediaDisplay
                      src={selectedBanner.imageUrl}
                      alt={selectedBanner.title}
                      className="w-full h-96 md:h-[500px]"
                      showControls={true}
                    />
                    
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-black mb-3">
                      {selectedBanner.title}
                    </h2>
                    <p className="text-gray-600 mb-4">
                      {selectedBanner.description}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>
                        Du {new Date(selectedBanner.startDate).toLocaleDateString('fr-FR')} au {new Date(selectedBanner.endDate).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default BannerCarousel
